import { IS_SOURCE_CONTROL_PERSONALISED_LOGOS_ENABLED } from '@/app-shell/flags/evidence-flags';
import { OfflineGatedTooltip } from '@/app-shell/offline-recording/offline-gated-tooltip';
import { offlineGatingMessages } from '@/app-shell/offline-recording/offline-gating-messages';
import { SourcesButton } from '@/components/chat/components/SourcesButton';
import DictateButton from '@/components/ui/dictate-button';
import type { PinnedChip } from '@/components/ui/floating-ask-heidi/evidence-settings-popover';
import { ProtocolsButton } from '@/components/ui/floating-ask-heidi/protocols-button';
import { cn } from '@/lib/cn';
import { isComposingEvent } from '@/utils/keyboardEventHelpers';
import { VoiceAssistantTrigger } from '@heidi/agents';
import { Button, type ButtonSize, type ButtonVariant } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { useActiveSourcesReveal } from '@heidi/evidence';
import { type Editor } from '@tiptap/react';
import { ArrowUp } from 'lucide-react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { memo, useEffect, useRef, type JSX, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { EvidenceOverlayChip, UseCaseChip } from './evidence-note-overlay.types';
import { UseCaseChipButton } from './use-case-chip-button';

export interface EvidenceNoteOverlayInputRowProps {
  inputPlaceholder: string;
  inputValue: string;
  isBarActive: boolean;
  autoFocusInput?: boolean;
  inactiveBarChip: UseCaseChip | EvidenceOverlayChip | null;
  onInputClick: () => void;
  onInputFocus: () => void;
  onInputValueChange: (value: string) => void;
  onInputSubmit: () => void;
  onInactiveChipClick: () => void;
  pinnedChips: PinnedChip[];
  isProtocolsMenuOpen: boolean;
  onProtocolsMenuOpenChange: (open: boolean) => void;
  onPinnedChipClick: (chip: PinnedChip) => void;
  onAddProtocol: (
    label: string,
    query: string,
    origin: 'library' | 'custom',
    description?: string
  ) => void;
  onEditProtocol: (
    chipId: string,
    label: string,
    query: string,
    description?: string
  ) => void;
  onRemoveProtocol: (chipId: string) => void;
  onRestoreDefaultShortcut: (shortcutId: string) => void;
  isAtShortcutLimit: boolean;
  shortcutCount: number;
  maxShortcuts: number;
  shadowClassName?: string;
  /** Override the default voice trigger (`undefined` = live trigger, `null` = hide). */
  voiceTrigger?: ReactNode;
  /** Every control here reaches the server, so the offline read-only policy
   * greys out the whole bar rather than one button. */
  isOfflineGated?: boolean;
}

export const EvidenceNoteOverlayInputRow = memo(
  ({
    inputPlaceholder,
    inputValue,
    isBarActive,
    autoFocusInput = false,
    inactiveBarChip,
    onInputClick,
    onInputFocus,
    onInputValueChange,
    onInputSubmit,
    onInactiveChipClick,
    pinnedChips,
    isProtocolsMenuOpen,
    onProtocolsMenuOpenChange,
    onPinnedChipClick,
    onAddProtocol,
    onEditProtocol,
    onRemoveProtocol,
    onRestoreDefaultShortcut,
    isAtShortcutLimit,
    shortcutCount,
    maxShortcuts,
    shadowClassName = 'shadow-sheet',
    voiceTrigger,
    isOfflineGated = false,
  }: EvidenceNoteOverlayInputRowProps) => {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const hasTypedInput = inputValue.trim().length > 0;
    const showActiveSources = useActiveSourcesReveal(isBarActive, !!inactiveBarChip);
    // GRO-2897: When the personalised-source-logos experiment is on, render
    // the avatar-stack SourcesButton on this surface in both idle and active
    // states (matching the floating Ask Heidi bar).
    const isPersonalisedLogosEnabled = IS_SOURCE_CONTROL_PERSONALISED_LOGOS_ENABLED();
    const prefersReducedMotion = useReducedMotion();
    const submitMicTransition = {
      duration: prefersReducedMotion ? 0 : 0.15,
      ease: 'easeOut' as const,
    };

    useEffect(() => {
      if (!autoFocusInput || !isBarActive) return;
      inputRef.current?.focus();
    }, [autoFocusInput, isBarActive]);

    return (
      <div
        data-testid="evidence-note-overlay-input-row"
        className={cn(
          'relative z-10 flex items-center justify-between',
          'h-15 w-full gap-3 px-4',
          'rounded-lg border border-border bg-surface',
          'transition-colors duration-200 hover:border-border-hover',
          'focus-within:outline-none focus-within:ring-border-hover',
          isOfflineGated && 'cursor-not-allowed',
          shadowClassName
        )}
        data-offline-gated={isOfflineGated ? 'true' : undefined}
      >
        <div className="text-text-secondary">
          <ProtocolsButton
            disabled={isOfflineGated}
            disabledTooltip={
              isOfflineGated
                ? intl.formatMessage(offlineGatingMessages.notAvailableOffline)
                : undefined
            }
            pinnedChips={pinnedChips}
            onPinnedChipClick={onPinnedChipClick}
            open={isProtocolsMenuOpen}
            onOpenChange={onProtocolsMenuOpenChange}
            onAddProtocol={onAddProtocol}
            onEditProtocol={onEditProtocol}
            onRemoveProtocol={onRemoveProtocol}
            onRestoreDefaultShortcut={onRestoreDefaultShortcut}
            isAtShortcutLimit={isAtShortcutLimit}
            shortcutCount={shortcutCount}
            maxShortcuts={maxShortcuts}
          />
        </div>
        <OfflineGatedTooltip
          isOfflineGated={isOfflineGated}
          className="relative min-w-0 flex-1"
        >
          <Input
            ref={inputRef}
            type="text"
            disabled={isOfflineGated}
            value={inputValue}
            onClick={onInputClick}
            onFocus={onInputFocus}
            onChange={(event) => {
              onInputValueChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !isComposingEvent(event.nativeEvent)) {
                event.preventDefault();
                onInputSubmit();
              }
            }}
            placeholder={inputPlaceholder}
            wrapperClassName="h-auto w-full gap-0 border-0 bg-transparent px-0 focus-within:border-0"
            className="h-auto cursor-text p-0 text-left text-text-primary placeholder:text-transparent"
            aria-label="Evidence prompt input"
          />
          <AnimatePresence initial={false}>
            {!inputValue && (
              <m.span
                key={inputPlaceholder}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 0.2, delay: 0.1 } }}
                className="pointer-events-none absolute inset-0 flex items-center text-text-tertiary"
                aria-hidden
              >
                <span className="block w-full truncate">{inputPlaceholder}</span>
              </m.span>
            )}
          </AnimatePresence>
        </OfflineGatedTooltip>
        <AnimatePresence initial={false}>
          {!isBarActive && inactiveBarChip && (
            <m.div
              initial={{ x: 12, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 12, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="shrink-0"
            >
              <OfflineGatedTooltip isOfflineGated={isOfflineGated}>
                <UseCaseChipButton
                  size="md"
                  chip={inactiveBarChip}
                  disabled={isOfflineGated}
                  onClick={onInactiveChipClick}
                />
              </OfflineGatedTooltip>
            </m.div>
          )}
        </AnimatePresence>
        <div className="flex shrink-0 items-center gap-1">
          {(isPersonalisedLogosEnabled || (isBarActive && showActiveSources)) && (
            <OfflineGatedTooltip isOfflineGated={isOfflineGated}>
              <SourcesButton
                popoverAlign="end"
                hideBadges
                disabled={isOfflineGated}
                personalisedSourceLogos={isPersonalisedLogosEnabled}
              />
            </OfflineGatedTooltip>
          )}
          <AnimatePresence mode="popLayout" initial={false}>
            {hasTypedInput ? (
              <m.div
                key="submit"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={submitMicTransition}
                className="inline-flex"
              >
                <OfflineGatedTooltip isOfflineGated={isOfflineGated}>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    iconOnly
                    disabled={isOfflineGated}
                    onClick={onInputSubmit}
                    aria-label="Submit evidence prompt"
                    className="bg-background-primary text-text-primary-invert"
                  >
                    <ArrowUp size={12} strokeWidth={2.5} />
                  </Button>
                </OfflineGatedTooltip>
              </m.div>
            ) : (
              <m.div
                key="dictate-voice"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={submitMicTransition}
                className="inline-flex items-center gap-1"
              >
                <OfflineGatedTooltip isOfflineGated={isOfflineGated}>
                  <DictateButton
                    size="sm"
                    inputRef={inputRef}
                    disabled={isOfflineGated}
                  />
                </OfflineGatedTooltip>
                {voiceTrigger !== undefined ? voiceTrigger : <VoiceAssistantTrigger />}
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }
);

EvidenceNoteOverlayInputRow.displayName = 'EvidenceNoteOverlayInputRow';

export interface ComposerDictateSlotProps {
  editor: Editor | null;
  /** Resolve the live TipTap instance at interaction time (avoids a null first paint). */
  getEditor?: () => Editor | null;
  disabled?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
  wrapperClassName?: string;
  buttonClassName?: string;
  onDictateClick?: () => void;
}

/** Shared empty-composer mic slot (Work home, project chat, etc.). */
export const ComposerDictateSlot = ({
  editor,
  getEditor,
  disabled = false,
  size = 'lg',
  variant = 'outline',
  wrapperClassName,
  buttonClassName,
  onDictateClick,
}: ComposerDictateSlotProps): JSX.Element => (
  <div className={cn('flex items-center justify-center', wrapperClassName)}>
    <DictateButton
      editor={editor}
      getEditor={getEditor}
      disabled={disabled}
      variant={variant}
      size={size}
      className={buttonClassName}
      onDictateClick={onDictateClick}
      preferInlineListening
    />
  </div>
);
